import { forwardRef, memo } from 'react'

import { Popover } from '../../../../overlays/Popover/base/Popover'
import { Flex } from '../../../../primitiveLayouts/Flex/base/Flex'
import { CalendarPicker } from '../../../datePickerUtils/subs/CalendarPicker'
import { DatePickerFocusWrap } from '../../../datePickerUtils/subs/DatePickerFocusWrap'
import { PickerPresetList } from '../../../datePickerUtils/subs/PickerPresets/PickerPresetList'
import { dateRangeConfig } from '../../../datePickerUtils/utils/dateRangeConfig'
import { useDatePickerOpened } from '../../../datePickerUtils/utils/useDatePickerOpened/useDatePickerOpened'

import type { DateRangePickerInputProps } from './DateRangePickerInput.types'
import { DateRangeCustomTargetWrap } from './subs/DateRangeCustomTargetWrap'
import { DateRangeTargetButton } from './subs/targets/DateRangeTargetButton'
import { DateRangeTargetInputLike } from './subs/targets/DateRangeTargetInputLike'

const DateRangePickerInputBase = memo(
  forwardRef<HTMLElement, DateRangePickerInputProps>(
    (
      {
        value,
        defaultValue,
        onChange,
        onBlur,
        presets,
        customTarget,

        /** Popover props */
        onOpenedChange,
        opened,
        dropdownPosition = 'bottom-start',

        /** InputWrap Props */
        label,
        disabled = false,
        className,
        required = false,
        errorMessage,
        desc,
        readOnly = false,
        rightSection,
        onClear,
        clearable = false,
        pickerOnly,

        /** Calendar Props */
        allowSingleDateInRange,
        maxLevel,
        placeholder,
        excludeDate,
        minDate,
        maxDate,
        defaultDate,
        ...other
      },
      forwardedRef
    ) => {
      const openedValues = useDatePickerOpened({ onOpenedChange, opened })
      const dateRangeSync = dateRangeConfig.useSyncDate({
        value,
        defaultValue,
        onBlur,
        onChange,
        minDate,
        maxDate,
        defaultDate
      })

      return (
        <Popover
          dropdownHeight='auto'
          dropdownWidth='auto'
          dropdownPosition={dropdownPosition}
          opened={openedValues.opened}
          disabled={disabled || readOnly}
        >
          <DatePickerFocusWrap setOpen={openedValues.setOpened}>
            <DateRangeCustomTargetWrap
              ref={forwardedRef}
              label={label}
              required={required}
              errorMessage={errorMessage}
              desc={desc}
              customTarget={customTarget}
              className={className}
              disabled={disabled}
              placeholder={placeholder}
              rightSection={rightSection}
              onClear={onClear}
              clearable={clearable}
              pickerOnly={pickerOnly}
              dateRangeSync={dateRangeSync}
              {...other}
            />
            <Popover.Dropdown>
              <Flex className='gap-5'>
                <CalendarPicker
                  value={dateRangeSync.value}
                  date={dateRangeSync.date}
                  onDateChange={dateRangeSync.onDateChange}
                  onChange={dateRangeSync.onChange}
                  minDate={dateRangeSync.minDate}
                  maxDate={dateRangeSync.maxDate}
                  defaultDate={dateRangeSync.defaultDate}
                  type='range'
                  componentType='date'
                  allowSingleDateInRange={allowSingleDateInRange}
                  numberOfColumns={2}
                  maxLevel={maxLevel}
                  excludeDate={excludeDate}
                  withWeekNumbers
                />
                <PickerPresetList
                  onChange={val => {
                    const startDate = new Date(val.start)
                    const endDate = new Date(val.end)
                    dateRangeSync.onDateChange(val.isPast ? val.end : val.start)
                    dateRangeSync.onChange([startDate, endDate])
                  }}
                  presets={presets}
                  value={dateRangeSync.strValue}
                />
              </Flex>
            </Popover.Dropdown>
          </DatePickerFocusWrap>
        </Popover>
      )
    }
  )
)

export type { DateRangePickerInputProps } from './DateRangePickerInput.types'

type CompoundDateRangePickerInputType = {
  Targets: {
    InputLike: typeof DateRangeTargetInputLike
    Button: typeof DateRangeTargetButton
  }
}

const TypedDateRangePickerInput =
  DateRangePickerInputBase as typeof DateRangePickerInputBase &
    CompoundDateRangePickerInputType

TypedDateRangePickerInput.Targets = {
  InputLike: DateRangeTargetInputLike,
  Button: DateRangeTargetButton
}

TypedDateRangePickerInput.displayName = 'DateRangePickerInput'
export const DateRangePickerInput = TypedDateRangePickerInput
